import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';

const API = window.location.origin + '/api';

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';
  const initialStatus = params.get('status') || '';
  const [state, setState] = useState({ loading: true, status: initialStatus, data: null });
  const navigate = useNavigate();

  useEffect(() => {
    if (!ref) { setState({ loading: false, status: 'failed', data: null }); return; }
    // Poll status a few times — the webhook may fire slightly after the browser redirect
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status`, { params: { ref } });
        if (data.found && (data.status === 'paid' || data.status === 'failed')) {
          setState({ loading: false, status: data.status === 'paid' ? 'success' : 'failed', data });
          return;
        }
        if (tries++ < 6) setTimeout(poll, 700);
        else setState({ loading: false, status: 'pending', data });
      } catch (err) {
        setState({ loading: false, status: 'failed', data: null });
      }
    };
    poll();
  }, [ref]);

  const isOrder = ref.startsWith('order_');
  const isSub = ref.startsWith('sub_');

  const meta = {
    success: { icon: CheckCircle2, color: '#065F46', bg: '#D1FAE5', title: 'Payment successful!',
               body: isOrder ? 'Your order is confirmed and being prepared.' : 'Subscription payment received. Thank you!' },
    failed:  { icon: XCircle,     color: '#991B1B', bg: '#FEE2E2', title: 'Payment did not go through',
               body: 'No charge was made. You can try again from your orders / dashboard.' },
    pending: { icon: Clock,       color: '#92400E', bg: '#FEF3C7', title: 'Payment is still processing',
               body: 'This can take up to a minute. Refresh in a moment to see the final status.' },
  }[state.status] || null;

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg,#0a1f44,#1e3a8a)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} data-testid="payment-callback-page">
      <div style={{ background: 'white', maxWidth: 440, width: '100%', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        {state.loading ? (
          <>
            <Loader2 size={30} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--navy)' }} />
            <p style={{ color: 'var(--text-muted)', marginTop: 14 }}>Confirming with the payment gateway…</p>
          </>
        ) : meta ? (
          <>
            <div style={{ background: meta.bg, color: meta.color, width: 68, height: 68, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <meta.icon size={30} />
            </div>
            <h1 style={{ margin: '0 0 8px', color: 'var(--navy)', fontSize: '1.4rem' }} data-testid="payment-title">{meta.title}</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{meta.body}</p>
            {state.data?.total !== undefined && (
              <p style={{ fontSize: '0.9rem', marginTop: 12 }}><strong>K{Number(state.data.total).toFixed(2)}</strong></p>
            )}
            {state.data?.amount !== undefined && (
              <p style={{ fontSize: '0.9rem', marginTop: 12 }}>
                <strong>K{Number(state.data.amount).toFixed(2)}</strong> · {state.data.month}
              </p>
            )}
            <button
              onClick={() => navigate(isOrder ? '/orders' : isSub ? '/admin/billing' : '/')}
              data-testid="payment-cta"
              style={{ marginTop: 22, padding: '10px 24px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              {isOrder ? 'View my orders' : isSub ? 'Back to billing' : 'Back to home'}
            </button>
          </>
        ) : (
          <p style={{ color: 'var(--text-muted)' }}>Missing payment reference.</p>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
